import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { User, BookOpen, Calendar, Phone, Mail, Award, MapPin, Users } from "lucide-react";
import { FaGraduationCap, FaBookOpen, FaPencilRuler } from "react-icons/fa";



import { studentService } from "../services/studentService";
import toast from "react-hot-toast";

function Dashboard() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const phone = localStorage.getItem("parentPhone");



      const response = await studentService.getEnrolledStudents(phone);
      // The backend returns enrollments, we need to map them to the format expected by the UI
      const formattedStudents = response.data.map(enrollment => ({
        id: enrollment.student?._id || enrollment._id,
        name: enrollment.student?.name || enrollment.student?.firstName || "N/A",
        regNo: enrollment?.uniqueRollNo || "N/A",
        phone: enrollment.student?.contactNumber || "N/A",
        email: enrollment.student?.email || "N/A",
        class: enrollment.class?.name || "N/A",
        rollNo: enrollment?.rollno || "N/A",
        section: enrollment.section || enrollment.student?.section || "A",
        academicSession: enrollment.academicSession?.label || "2024-2025",
        parentsName: enrollment.student?.fatherName || "N/A",
        imageUrl: enrollment.student?.profilePicture || enrollment.student?.profileImage || "/un.png",
      }));
      setStudents(formattedStudents);
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error("Failed to load students");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    try {
      setLoading(true);
      const response = await studentService.searchStudents(query);
      // The search endpoint returns students with an enrollments array
      const formattedStudents = response.data.map(student => {
        const primaryEnrollment = student.enrollments?.[0] || {};
        return {
          id: student._id,
          name: student.name || student.firstName || "N/A",
          regNo: student.uniqueRollNo || primaryEnrollment.uniqueRollNo || "N/A",
          phone: student.contactNumber || "N/A",
          email: student.email || "N/A",
          class: primaryEnrollment.class?.name || "N/A",
          rollNo: primaryEnrollment.rollno || student.rollNumber || "N/A",
          section: primaryEnrollment.section || student.section || "A",
          academicSession: primaryEnrollment.academicSession?.label || "2024-2025",
          parentsName: student.fatherName || "N/A",
          imageUrl: student.profilePicture || student.profileImage || "/un.png",
        };
      });
      setStudents(formattedStudents);
    } catch (error) {
      console.error("Search error:", error);
      toast.error(error.response?.data?.message || "Search failed");
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-pragyaBlue/10 pb-32 pt-6 px-4 font-sans text-slate-800">
      <header className="mb-8 max-w-4xl mx-auto text-center">
        <motion.h1
          className="text-3xl font-bold mb-2 text-slate-900"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          My Dashboard
        </motion.h1>
        <motion.p
          className="text-slate-500 text-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          Welcome Back, Future Leader
        </motion.p>
      </header>

      <div className="max-w-4xl mx-auto mb-10">
        <div className="relative group">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <User size={18} className="text-slate-400 group-focus-within:text-pragyaBlue transition-colors" />
          </div>
          <input
            type="text"
            placeholder="Search students by name or contact number..."
            className="w-full pl-11 pr-32 py-4 bg-white border border-slate-200 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-pragyaBlue/20 focus:border-pragyaBlue transition-all"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearch(e.target.value);
              }
            }}
            onChange={(e) => {
              if (e.target.value.length === 0) {
                fetchStudents();
              }
            }}
          />
          <div className="absolute inset-y-2 right-2">
            <button
              className="h-full px-6 bg-pragyaBlue text-white rounded-xl font-semibold shadow-md hover:bg-pragyaBlue/90 transition-colors"
              onClick={() => {
                const input = document.querySelector('input[placeholder="Search students by name or contact number..."]');
                if (input.value) handleSearch(input.value);
              }}
            >
              Search
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="relative w-32 h-32 mb-8">
            {/* Background Glow */}
            <motion.div
              className="absolute inset-0 bg-pragyaBlue/10 rounded-full blur-2xl"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 0.8, 0.5]
              }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* Central Icon */}
            <motion.div
              className="absolute inset-0 flex items-center justify-center bg-white rounded-3xl shadow-xl border border-slate-50 z-10"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <FaGraduationCap className="text-4xl text-pragyaBlue" />
            </motion.div>

            {/* Orbiting Icons */}
            <motion.div
              className="absolute -top-4 -right-4 w-10 h-10 bg-pragyaOrange/10 rounded-xl flex items-center justify-center text-pragyaOrange"
              animate={{ rotate: 360 }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            >
              <FaBookOpen />
            </motion.div>

            <motion.div
              className="absolute -bottom-2 -left-4 w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center text-green-600"
              animate={{ rotate: -360 }}
              transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
            >
              <FaPencilRuler />
            </motion.div>
          </div>

          <div className="text-center">
            <motion.h3
              className="text-lg font-bold text-slate-800 mb-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              Loading Potential Leaders
            </motion.h3>

            <div className="flex flex-col items-center gap-2">
              <div className="w-48 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-pragyaBlue"
                  initial={{ width: "0%" }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                />
              </div>
              <motion.p
                className="text-xs text-slate-400 font-medium"
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                Connecting to education portal...
              </motion.p>
            </div>
          </div>
        </div>
      ) : students.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-slate-300 max-w-4xl mx-auto">
          <Users size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-bold text-slate-700">No Students Found</h3>
          <p className="text-slate-500">Try a different contact number or check enrollment status.</p>
          <button
            onClick={fetchStudents}
            className="mt-6 text-pragyaBlue font-semibold hover:underline"
          >
            Show All Enrolled Students
          </button>
        </div>
      ) : (
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">

          {students.map((student, index) => (
            <motion.div
              key={student.id}
              className="group relative bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-lg hover:shadow-xl hover:border-pragyaBlue/30 transition-all duration-300"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.15, duration: 0.6, type: "spring", stiffness: 100 }}
            >
              {/* Header / Profile Summary */}
              <div className="p-6 flex flex-col items-center relative z-10">
                <div className="relative mb-4">
                  <div className="absolute inset-0 bg-pragyaBlue/10 rounded-full blur-xl transform group-hover:scale-110 transition-transform duration-500"></div>
                  <img
                    src={student.imageUrl}
                    alt={student.name}
                    className="w-24 h-24 rounded-full border-4 border-white shadow-md relative z-10 object-cover ring-1 ring-slate-100"
                  />
                </div>

                <h2 className="text-xl font-bold text-center tracking-wide mb-1 text-slate-900">{student.name}</h2>
                <div className="flex items-center space-x-2 text-sm text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
                  <span className="font-semibold text-pragyaBlue">{student.class} - {student.section}</span>
                  <span className="text-slate-400">•</span>
                  <span>Roll: {student.rollNo}</span>
                </div>
              </div>

              {/* Content Details */}
              <div className="px-6 pb-6 space-y-3 relative z-10">
                <div className="bg-slate-50 rounded-xl p-4 space-y-3 border border-slate-100">
                  <InfoRow icon={User} label="Reg No" value={student.regNo} />
                  <InfoRow icon={Calendar} label="Session" value={student.academicSession} />
                  <InfoRow icon={Phone} label="Phone" value={student.phone} />
                  <InfoRow icon={User} label="Guardian" value={student.parentsName} />
                </div>

                <motion.button
                  onClick={() => {
                    localStorage.setItem("selectedStudentId", student.id);
                    // Also update sessionStorage if needed, though profile page should fetch fresh data
                    navigate("/profile");
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full mt-4 bg-gradient-to-r from-pragyaOrange to-pragyaOrange text-white font-semibold py-3 rounded-xl shadow-lg hover:shadow-pragyaOrange/30 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <User size={18} />
                  <span>View Full Profile</span>
                </motion.button>
              </div>

              {/* Decorative subtle background blobs */}
              <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 rounded-full bg-pragyaBlue/5 blur-3xl opacity-50" />
              <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-64 h-64 rounded-full bg-pragyaOrange/5 blur-3xl opacity-50" />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

// Helper component for uniform rows
function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <div className="flex items-center text-slate-500 gap-2">
        <Icon size={14} className="text-pragyaBlue" />
        <span>{label}</span>
      </div>
      <span className="font-medium text-slate-800 truncate max-w-[150px] text-right">{value}</span>
    </div>
  );
}

export default Dashboard;
